import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { AgentModelCard } from "@/components/dashboard/AgentSection";
import {
  clampEffort,
  DEFAULT_REASONING_EFFORT,
  DEFAULT_REASONING_MODEL_ID,
  modelById,
} from "@domain/model-catalog";

import type { ReasoningSelection } from "@/components/dashboard/AgentSection";
import type { Plan } from "@domain/claw-config";

export const Route = createFileRoute("/dev/model-switcher-preview")({
  component: DevModelSwitcherPreview,
});

const PLANS: readonly { id: Plan; label: string }[] = [
  { id: "trial", label: "Trial" },
  { id: "standard", label: "Standard" },
  { id: "byok", label: "BYOK" },
];

const DEFAULT_SELECTION: ReasoningSelection = {
  modelId: DEFAULT_REASONING_MODEL_ID,
  effort: DEFAULT_REASONING_EFFORT,
};

/** Dev-only harness — renders the settings Agent card without the auth guard
 *  so the gating + draft/Revert/Update UX can be iterated on (and
 *  screenshotted) without a Google session. The plan toggle demos gating on
 *  Trial/Standard and full unlock on BYOK. "Saved" state and the update are
 *  local mock state; nothing here touches the pod or Convex. Inert outside
 *  `vite dev`. */
function DevModelSwitcherPreview() {
  const [plan, setPlan] = useState<Plan>("trial");
  const [saved, setSaved] = useState<ReasoningSelection>(DEFAULT_SELECTION);
  const [draft, setDraft] = useState<ReasoningSelection | null>(null);
  const [note, setNote] = useState<string | null>(null);
  if (!import.meta.env.DEV) return null;

  const current = draft ?? saved;
  const dirty =
    current.modelId !== saved.modelId || current.effort !== saved.effort;

  return (
    <div className="min-h-screen bg-home-cream font-body text-home-ink dark:bg-surface-dark dark:text-white">
      <div className="flex h-16 items-center justify-between border-b border-black/10 px-6 dark:border-white/10">
        <span className="text-xs font-bold tracking-wider text-black/40 uppercase dark:text-white/40">
          Dev preview — Agent model card
        </span>
        <div className="inline-flex gap-0.5 rounded-full bg-black/5 p-0.5 dark:bg-white/10">
          {PLANS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPlan(p.id)}
              className={`rounded-full px-3 py-1 text-[12px] font-semibold transition-colors ${
                plan === p.id
                  ? "bg-white text-black shadow-sm dark:bg-surface-dark-card dark:text-white"
                  : "text-black/55 dark:text-white/55"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-2xl px-4 py-10">
        <h2 className="mb-3 text-[12px] font-bold tracking-[0.14em] text-black/40 uppercase dark:text-white/40">
          In settings — Agent · Model
        </h2>
        <AgentModelCard
          plan={plan}
          modelId={current.modelId}
          effort={current.effort}
          saved={saved}
          onSelectModel={(id) =>
            setDraft({
              modelId: id,
              effort: clampEffort({ model: modelById(id), effort: current.effort }),
            })
          }
          onSelectEffort={(effort) =>
            setDraft({ modelId: current.modelId, effort })
          }
          dirty={dirty}
          pending={false}
          onRevert={() => setDraft(null)}
          onUpdate={() => {
            setSaved(current);
            setDraft(null);
            setNote("restarting your agent (mock)");
            setTimeout(() => setNote(null), 2200);
          }}
          switchingNote={note}
          onManagePlan={() => window.alert("Upgrade path → Subscription section")}
        />
      </div>
    </div>
  );
}
